const API_URL = process.env.REACT_APP_API_URL || '';

export interface Clip {
  id: number;
  title: string;
  content: string;
  position?: number;
  createdAt: string;
  updatedAt: string;
}

export interface Upload {
  id: number;
  page_id: string;
  filename: string;
  object_key: string;
  file_type: string;
  file_size: number;
  created_at: number;
  downloadUrl: string;
}

export interface ClipboardPage {
  id: string;
  clips: Clip[];
  createdAt: string;
  expiresAt: string;
  isLocked: boolean;
}

class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
  }
}

async function fetchAPI(endpoint: string, options: RequestInit = {}) {
  const response = await fetch(`${API_URL}${endpoint}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Request failed' }));
    throw new ApiError(response.status, error.message || error.error);
  }

  return response.json();
}

export interface SlugAvailability {
  available: boolean;
  reason?: string;
}

export const api = {
  createPage: async (customSlug?: string): Promise<ClipboardPage> => {
    return fetchAPI('/api/pages', {
      method: 'POST',
      body: customSlug ? JSON.stringify({ id: customSlug }) : undefined,
    });
  },

  checkSlug: async (slug: string): Promise<SlugAvailability> => {
    return fetchAPI(`/api/pages/availability/${encodeURIComponent(slug)}`);
  },

  getPage: async (id: string): Promise<ClipboardPage> => {
    return fetchAPI(`/api/pages/${id}`);
  },

  extendPage: async (id: string): Promise<ClipboardPage> => {
    return fetchAPI(`/api/pages/${id}/extend`, { method: 'POST' });
  },

  unlockPage: async (id: string): Promise<ClipboardPage> => {
    return fetchAPI(`/api/pages/${id}/unlock`, { method: 'POST' });
  },

  createClip: async (pageId: string, title: string, content: string): Promise<Clip> => {
    return fetchAPI(`/api/pages/${pageId}/clips`, {
      method: 'POST',
      body: JSON.stringify({ title, content }),
    });
  },

  updateClip: async (pageId: string, clipId: number, title: string, content: string): Promise<Clip> => {
    return fetchAPI(`/api/pages/${pageId}/clips/${clipId}`, {
      method: 'PUT',
      body: JSON.stringify({ title, content }),
    });
  },

  deleteClip: async (pageId: string, clipId: number): Promise<void> => {
    return fetchAPI(`/api/pages/${pageId}/clips/${clipId}`, { method: 'DELETE' });
  },

  deleteAllClips: async (pageId: string): Promise<void> => {
    return fetchAPI(`/api/pages/${pageId}/clips`, { method: 'DELETE' });
  },

  reorderClips: async (pageId: string, clipOrder: number[]): Promise<{ clips: Clip[] }> => {
    return fetchAPI(`/api/pages/${pageId}/clips/reorder`, {
      method: 'POST',
      body: JSON.stringify({ clipOrder }),
    });
  },

  getUploads: async (pageId: string): Promise<Upload[]> => {
    return fetchAPI(`/api/pages/${pageId}/uploads`);
  },

  uploadFile: async (pageId: string, file: File): Promise<Upload> => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await fetch(`${API_URL}/api/pages/${pageId}/uploads`, {
      method: 'POST',
      body: formData,
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Upload failed' }));
      throw new ApiError(response.status, error.message || error.error);
    }
    return response.json();
  },

  deleteUpload: async (pageId: string, uploadId: number): Promise<void> => {
    return fetchAPI(`/api/pages/${pageId}/uploads/${uploadId}`, { method: 'DELETE' });
  },

  renameUpload: async (pageId: string, uploadId: number, filename: string): Promise<Upload> => {
    return fetchAPI(`/api/pages/${pageId}/uploads/${uploadId}`, {
      method: 'PATCH',
      body: JSON.stringify({ filename }),
    });
  },

  fetchUrl: async (pageId: string, url: string): Promise<{ fetchId: string }> => {
    return fetchAPI(`/api/pages/${pageId}/uploads/fetch`, {
      method: 'POST',
      body: JSON.stringify({ url }),
    });
  },
};
